export const getClientEarningsRequest = (clientId: number, from: string, to: string) => ({
	method: "reports/client/earnings",
	apiVersion: 2 as const,
	headers: { ignoreBusy: "true" },
	data: { client_id: clientId, from, to },
});

export interface ClientEarningsMetrics {
	turnover: number;
	fees: number;
	rollingTaken: number;
	rollingReleased: number;
	refunds: number;
	chargebacks: number;
	net: number;
	withdrawal?: number;
	setup?: number;
	monthly?: number;
	/** OCT payouts; older responses omit it. */
	oct?: number;
}

export interface ClientEarningsChannel {
	id: number;
	name: string;
	currencies: Record<string, ClientEarningsMetrics>;
}

export interface ClientEarningsStatement {
	id: number;
	type: string;
	status: string;
	periodFrom: string;
	periodTo: string;
	fullyInsidePeriod: boolean;
	net: Record<string, number>;
}

export interface ClientDeferredStatement {
	id: number;
	type: string;
	status: string;
	periodFrom: string;
	periodTo: string;
	amount: Record<string, number>;
	count: number;
}

export interface ClientUnsettledTransaction {
	/** Kept as the server's string: payment ids may outgrow safe integers. */
	id: string;
	createdAt: string;
	amount: number;
	currency: string;
	status: string;
	/** Age computed by the server in UTC; older responses omit it. */
	ageDays?: number;
}

export interface ClientLateSettledTransaction extends ClientUnsettledTransaction {
	billedBy: { id: string } | null;
}

export interface ClientLateSettled {
	billedLater: Record<string, number>;
	transactions: ClientLateSettledTransaction[];
	truncated: boolean;
	total?: number;
}

export interface ClientAmountCount {
	amount: number;
	count: number;
}

export interface ClientUnsettled {
	byCurrency: Record<string, ClientAmountCount>;
	byStatus: Record<string, Record<string, ClientAmountCount>>;
	transactions: ClientUnsettledTransaction[];
	total: number;
	truncated: boolean;
	/** Server split: older than the client's settlement window (worth chasing) and still within it (normal). */
	stuck?: { byCurrency: Record<string, ClientAmountCount>; count: number };
	recent?: { byCurrency: Record<string, ClientAmountCount>; count: number };
	stuckAfterDays?: number;
	stuckAfterSource?: string;
}

export interface ClientNotPaidOut {
	notBilled?: Record<string, number>;
	total: Record<string, number>;
	deferred: Record<string, number>;
	unsettled: Record<string, number>;
}

export interface ClientEarningsData {
	clientId: number;
	period: { from: string; to: string };
	/** The last day both billed and reported; days after it are the open tail. */
	closedThrough?: string;
	openTail?: { days: number; from: string; byCurrency: Record<string, ClientAmountCount>; note?: string };
	turnoverInRange?: Record<string, ClientAmountCount>;
	/** Payments billed by this period's documents whose daily report is dated after the period. */
	reportedLater?: { byCurrency: Record<string, ClientAmountCount>; note?: string };
	/** Payments created before the period whose daily report falls inside it; the other half of the settlement shift. */
	reportedEarlier?: { byCurrency: Record<string, ClientAmountCount>; note?: string };
	unsettled?: ClientUnsettled;
	accrued: {
		byCurrency: Record<string, ClientEarningsMetrics>;
		channels: ClientEarningsChannel[];
		daysCovered: number;
	};
	paid: {
		byCurrency: Record<string, ClientEarningsMetrics>;
		byPeriod?: {
			inside: Record<string, ClientEarningsMetrics>;
			outside: Record<string, ClientEarningsMetrics>;
			outsideDays: string[];
		};
		channels: ClientEarningsChannel[];
		statements: ClientEarningsStatement[];
		pending: Record<string, unknown>;
		deferred?: {
			out: Record<string, number>;
			/** Billed by documents that reach past the period's end (weekly edges); older responses omit it. */
			beyond: Record<string, number>;
			in: Record<string, number>;
			outCount: number;
			inCount: number;
			outStatements: ClientDeferredStatement[];
			inStatements: ClientDeferredStatement[];
			note?: string;
		};
		notPaidOut?: ClientNotPaidOut;
		lateSettled?: ClientLateSettled;
	};
	reconciliation: {
		uncoveredDays: number;
		notes: string[];
		turnoverUnexplained?: Record<string, number>;
		reportsUnexplained?: Record<string, number>;
		documentsRounding?: Record<string, number>;
	};
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isAmount = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isCount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const isDate = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const parseMetrics = (value: unknown, paid: boolean): ClientEarningsMetrics | null => {
	if (!isRecord(value) || !isAmount(value.turnover) || !isAmount(value.fees) || !isAmount(value.rolling_taken)
		|| !isAmount(value.rolling_released) || !isAmount(value.refunds) || !isAmount(value.chargebacks) || !isAmount(value.net)) return null;
	if (paid && (!isAmount(value.withdrawal) || !isAmount(value.setup) || !isAmount(value.monthly))) return null;
	return {
		turnover: value.turnover,
		fees: value.fees,
		rollingTaken: value.rolling_taken,
		rollingReleased: value.rolling_released,
		refunds: value.refunds,
		chargebacks: value.chargebacks,
		net: value.net,
		...(isAmount(value.oct) ? { oct: value.oct } : {}),
		...(paid ? { withdrawal: value.withdrawal as number, setup: value.setup as number, monthly: value.monthly as number } : {}),
	};
};

const parseCurrencies = (value: unknown, paid: boolean, allowEmpty = false): Record<string, ClientEarningsMetrics> | null => {
	if (!isRecord(value)) return null;
	const currencies: Record<string, ClientEarningsMetrics> = {};
	for (const [currency, rawMetrics] of Object.entries(value)) {
		if (!/^[A-Z]{3}$/.test(currency)) return null;
		const metrics = parseMetrics(rawMetrics, paid);
		if (!metrics) return null;
		currencies[currency] = metrics;
	}
	return allowEmpty || Object.keys(currencies).length ? currencies : null;
};

const parseChannels = (value: unknown): ClientEarningsChannel[] | null => {
	if (!Array.isArray(value)) return null;
	const channels: ClientEarningsChannel[] = [];
	for (const item of value) {
		if (!isRecord(item) || !isCount(item.channel_id) || typeof item.name !== "string") return null;
		const currencies = parseCurrencies(item.currencies, false);
		if (!currencies) return null;
		channels.push({ id: item.channel_id, name: item.name.trim() || "—", currencies });
	}
	return channels;
};

const parseStatements = (value: unknown): ClientEarningsStatement[] | null => {
	if (!Array.isArray(value)) return null;
	const statements: ClientEarningsStatement[] = [];
	for (const item of value) {
		if (!isRecord(item) || !isCount(item.id) || typeof item.type !== "string" || typeof item.status !== "string"
			|| !isDate(item.period_start) || !isDate(item.period_end) || typeof item.fully_inside_period !== "boolean" || !isRecord(item.net)) return null;
		const net: Record<string, number> = {};
		for (const [currency, amount] of Object.entries(item.net)) {
			if (!/^[A-Z]{3}$/.test(currency) || !isAmount(amount)) return null;
			net[currency] = amount;
		}
		statements.push({ id: item.id, type: item.type, status: item.status, periodFrom: item.period_start, periodTo: item.period_end, fullyInsidePeriod: item.fully_inside_period, net });
	}
	return statements;
};

const parseDeferredStatements = (value: unknown): ClientDeferredStatement[] | null => {
	// Older responses do not include statement attribution.
	if (value === undefined) return [];
	if (!Array.isArray(value)) return null;
	const statements: ClientDeferredStatement[] = [];
	for (const item of value) {
		if (!isRecord(item) || !isCount(item.id) || typeof item.type !== "string" || typeof item.status !== "string"
			|| typeof item.period_start !== "string" || typeof item.period_end !== "string"
			|| !isRecord(item.amount) || !isCount(item.count)) return null;
		const amount: Record<string, number> = {};
		for (const [currency, total] of Object.entries(item.amount)) {
			if (!/^[A-Z]{3}$/.test(currency) || !isAmount(total)) return null;
			amount[currency] = total;
		}
		statements.push({ id: item.id, type: item.type, status: item.status, periodFrom: item.period_start, periodTo: item.period_end, amount, count: item.count });
	}
	return statements;
};

const parseAmounts = (value: unknown): Record<string, number> | null => {
	if (!isRecord(value)) return null;
	const amounts: Record<string, number> = {};
	for (const [currency, amount] of Object.entries(value)) {
		if (!/^[A-Z]{3}$/.test(currency) || !isAmount(amount)) return null;
		amounts[currency] = amount;
	}
	return amounts;
};

const parseAmountCounts = (value: unknown): Record<string, ClientAmountCount> | null => {
	if (!isRecord(value)) return null;
	const totals: Record<string, ClientAmountCount> = {};
	for (const [currency, total] of Object.entries(value)) {
		if (!/^[A-Z]{3}$/.test(currency) || !isRecord(total) || !isAmount(total.amount) || !isCount(total.count)) return null;
		totals[currency] = { amount: total.amount, count: total.count };
	}
	return totals;
};

// The not-paid-out blocks below are newer than the rest of the report: a malformed one is dropped instead of failing the whole card.
const parseNotPaidOut = (value: unknown): ClientNotPaidOut | undefined => {
	if (!isRecord(value)) return undefined;
	const total = parseAmounts(value.by_currency);
	const deferred = parseAmounts(value.deferred);
	const unsettled = parseAmounts(value.unsettled);
	const notBilled = parseAmounts(value.not_billed) ?? undefined;
	return total && deferred && unsettled ? { total, deferred, unsettled, ...(notBilled ? { notBilled } : {}) } : undefined;
};

export const parseLateSettled = (value: unknown): ClientLateSettled | undefined => {
	if (!isRecord(value) || !Array.isArray(value.transactions)) return undefined;
	const billedLater = parseAmounts(value.billed_later);
	if (!billedLater) return undefined;
	const transactions: ClientLateSettledTransaction[] = [];
	for (const item of value.transactions) {
		if (!isRecord(item)) return undefined;
		const id = typeof item.id === "string" || isCount(item.id) ? String(item.id).trim() : "";
		const billedId = isRecord(item.billed_by) && (typeof item.billed_by.id === "string" || isCount(item.billed_by.id)) ? String(item.billed_by.id).trim() : "";
		if (!id || !isAmount(item.amount) || typeof item.currency !== "string" || !/^[A-Z]{3}$/.test(item.currency)
			|| (item.billed_by !== null && !billedId)) return undefined;
		transactions.push({
			id, amount: item.amount, currency: item.currency,
			createdAt: typeof item.created_at === "string" ? item.created_at : "",
			status: typeof item.settlement_status === "string" ? item.settlement_status : "",
			billedBy: item.billed_by === null ? null : { id: billedId },
		});
	}
	return { billedLater, transactions, truncated: value.truncated === true, ...(isCount(value.total) ? { total: value.total } : {}) };
};

/** Server totals take precedence; never derive money from a potentially truncated transaction list. */
export const getDeferredOutAmounts = (paid: ClientEarningsData["paid"]): Record<string, number> => {
	const out = paid.deferred?.out ?? {};
	const later = paid.lateSettled?.billedLater ?? {};
	return Object.fromEntries([...new Set([...Object.keys(out), ...Object.keys(later), ...Object.keys(paid.notPaidOut?.deferred ?? {})])]
		.map(code => [code, paid.notPaidOut?.deferred[code] ?? ((out[code] ?? 0) + (later[code] ?? 0))]));
};

const parseUnsettled = (value: unknown): ClientUnsettled | undefined => {
	if (!isRecord(value) || !isRecord(value.by_status) || !Array.isArray(value.transactions)) return undefined;
	const byCurrency = parseAmountCounts(value.by_currency);
	if (!byCurrency) return undefined;
	const byStatus: ClientUnsettled["byStatus"] = {};
	for (const [status, totals] of Object.entries(value.by_status)) {
		const parsed = parseAmountCounts(totals);
		if (!parsed) return undefined;
		byStatus[status] = parsed;
	}
	const transactions: ClientUnsettledTransaction[] = [];
	for (const item of value.transactions) {
		const id = isRecord(item) && (typeof item.id === "string" || isCount(item.id)) ? String(item.id).trim() : "";
		if (!isRecord(item) || !id || typeof item.created_at !== "string"
			|| !isAmount(item.amount) || typeof item.currency !== "string" || !/^[A-Z]{3}$/.test(item.currency)
			|| (item.settlement_status !== undefined && item.settlement_status !== null && typeof item.settlement_status !== "string")) return undefined;
		transactions.push({
			id, createdAt: item.created_at, amount: item.amount, currency: item.currency,
			status: typeof item.settlement_status === "string" ? item.settlement_status : "",
			...(isCount(item.age_days) ? { ageDays: item.age_days } : {}),
		});
	}
	const parseGroup = (raw: unknown) => {
		const amounts = isRecord(raw) ? parseAmountCounts(raw.by_currency) : null;
		return amounts && isRecord(raw) && isCount(raw.count) ? { byCurrency: amounts, count: raw.count } : undefined;
	};
	const stuck = parseGroup(value.stuck);
	const recent = parseGroup(value.recent);
	const split = stuck && recent ? {
		stuck, recent,
		...(isCount(value.stuck_after_days) ? { stuckAfterDays: value.stuck_after_days } : {}),
		...(typeof value.stuck_after_source === "string" ? { stuckAfterSource: value.stuck_after_source } : {}),
	} : {};
	const count = Object.values(byCurrency).reduce((sum, total) => sum + total.count, 0);
	return { byCurrency, byStatus, transactions, total: isCount(value.total) ? value.total : count, truncated: value.truncated === true, ...split };
};

export const parseClientEarnings = (value: unknown, clientId: number, from: string, to: string): ClientEarningsData | null => {
	if (!isRecord(value) || value.client_id !== clientId || !isRecord(value.period) || value.period.from !== from || value.period.to !== to
		|| !isDate(value.period.from) || !isDate(value.period.to) || !isRecord(value.accrued) || !isRecord(value.paid)
		|| !isRecord(value.reconciliation) || !isCount(value.accrued.days_covered) || !isRecord(value.paid.pending)
		|| !isCount(value.reconciliation.uncovered_days) || !Array.isArray(value.reconciliation.notes)
		|| value.reconciliation.notes.some(note => typeof note !== "string")) return null;
	// A client with no activity in the range gets empty maps, and that is a valid answer, not a broken one.
	const accruedCurrencies = parseCurrencies(value.accrued.by_currency, false, true);
	const accruedChannels = parseChannels(value.accrued.by_channel);
	const paidCurrencies = parseCurrencies(value.paid.by_currency, true, true);
	const paidChannels = parseChannels(value.paid.by_channel);
	const statements = parseStatements(value.paid.statements);
	if (!accruedCurrencies || !accruedChannels || !paidCurrencies || !paidChannels || !statements) return null;
	let byPeriod: ClientEarningsData["paid"]["byPeriod"];
	let deferred: ClientEarningsData["paid"]["deferred"];
	if (value.paid.deferred !== undefined) {
		const raw = value.paid.deferred;
		if (!isRecord(raw) || !isRecord(raw.out) || !isRecord(raw.in)
			|| !isCount(raw.out_count) || !isCount(raw.in_count)
			|| (raw.note !== undefined && raw.note !== null && typeof raw.note !== "string")) return null;
		for (const amounts of [raw.out, raw.in]) {
			if (Object.entries(amounts).some(([currency, amount]) => !/^[A-Z]{3}$/.test(currency) || !isAmount(amount))) return null;
		}
		const outStatements = parseDeferredStatements(raw.out_statements);
		const inStatements = parseDeferredStatements(raw.in_statements);
		if (!outStatements || !inStatements) return null;
		deferred = {
			out: { ...raw.out } as Record<string, number>, in: { ...raw.in } as Record<string, number>,
			beyond: parseAmounts(raw.beyond) ?? {},
			outCount: raw.out_count, inCount: raw.in_count,
			outStatements, inStatements,
			...(typeof raw.note === "string" && raw.note.trim() ? { note: raw.note } : {}),
		};
	}
	if (value.paid.by_period !== undefined) {
		const rawPeriod = value.paid.by_period;
		if (!isRecord(rawPeriod) || !Array.isArray(rawPeriod.outside_days) || !rawPeriod.outside_days.every(isDate)) return null;
		const inside = parseCurrencies(rawPeriod.inside, false, true);
		const outside = parseCurrencies(rawPeriod.outside, false, true);
		if (!inside || !outside) return null;
		byPeriod = { inside, outside, outsideDays: rawPeriod.outside_days };
	}
	const notPaidOut = parseNotPaidOut(value.paid.not_paid_out);
	const lateSettled = parseLateSettled(value.paid.late_settled);
	const unsettled = parseUnsettled(value.unsettled);
	const turnoverInRange = parseAmountCounts(value.turnover_in_range) ?? undefined;
	const turnoverUnexplained = parseAmounts(value.reconciliation.turnover_unexplained) ?? undefined;
	// Renamed from reported_later_unexplained on 2026-10-26; the old name is still read from older builds.
	// An explicit null is meaningful (an open period cannot be reconciled), so the old name is read only when the new key is absent.
	const reportsUnexplained = parseAmounts("reports_unexplained" in value.reconciliation
		? value.reconciliation.reports_unexplained : value.reconciliation.reported_later_unexplained) ?? undefined;
	const documentsRounding = parseAmounts(value.reconciliation.documents_rounding) ?? undefined;
	const parseShift = (raw: unknown) => {
		const amounts = isRecord(raw) ? parseAmountCounts(raw.by_currency) : null;
		return amounts && isRecord(raw)
			? { byCurrency: amounts, ...(typeof raw.note === "string" && raw.note.trim() ? { note: raw.note } : {}) }
			: undefined;
	};
	const reportedLater = parseShift(value.reported_later);
	const reportedEarlier = parseShift(value.reported_earlier);
	const closedThrough = isDate(value.period.closed_through) ? value.period.closed_through : undefined;
	const rawTail = isRecord(value.open_tail) ? value.open_tail : undefined;
	const tailAmounts = rawTail ? parseAmountCounts(rawTail.by_currency) : null;
	const openTail = rawTail && tailAmounts && isCount(rawTail.days) && isDate(rawTail.from) ? {
		days: rawTail.days, from: rawTail.from, byCurrency: tailAmounts,
		...(typeof rawTail.note === "string" && rawTail.note.trim() ? { note: rawTail.note } : {}),
	} : undefined;
	return {
		clientId,
		period: { from, to },
		...(turnoverInRange ? { turnoverInRange } : {}),
		...(reportedLater ? { reportedLater } : {}),
		...(reportedEarlier ? { reportedEarlier } : {}),
		...(closedThrough ? { closedThrough } : {}),
		...(openTail ? { openTail } : {}),
		...(unsettled ? { unsettled } : {}),
		accrued: { byCurrency: accruedCurrencies, channels: accruedChannels, daysCovered: value.accrued.days_covered },
		paid: { byCurrency: paidCurrencies, ...(lateSettled ? { lateSettled } : {}), ...(byPeriod ? { byPeriod } : {}), ...(deferred ? { deferred } : {}), ...(notPaidOut ? { notPaidOut } : {}), channels: paidChannels, statements, pending: value.paid.pending },
		reconciliation: {
			uncoveredDays: value.reconciliation.uncovered_days, notes: value.reconciliation.notes as string[],
			...(turnoverUnexplained ? { turnoverUnexplained } : {}),
			...(reportsUnexplained ? { reportsUnexplained } : {}),
			...(documentsRounding ? { documentsRounding } : {}),
		},
	};
};
